import React, { useCallback, useEffect, useState } from 'react';
import { useCpPortal } from '../../hooks/useCpPortal';
import { BookOpen, Loader2, FileCode, Download, FlaskConical } from 'lucide-react';

type Section = 'overview' | 'reference' | 'testdata';

interface ServerInfo {
  label: string;
  url: string;
  note?: string;
}

interface OverviewPayload {
  specVersion?: string;
  title?: string;
  summary?: string;
  servers?: ServerInfo[];
  auth?: {
    authorizationHeader?: string;
    timestampHeader?: string;
    replayWindowSeconds?: number;
    hmacKey?: string;
  };
  sets?: Record<string, { label?: string; kyc?: string; gate?: string; endpoints?: string[] }>;
  operations?: { method: string; path: string; summary?: string }[];
}

interface TestDataPayload {
  environments?: Record<string, { label?: string; host?: string; note?: string }>;
  items?: {
    set: string;
    product: string;
    endpoints: string[];
    fields: { label: string; value: string }[];
    note?: string;
  }[];
}

const DOWNLOADS: { id: string; href: string; filename: string; label: string }[] = [
  { id: 'openapi-json', href: '/openapi.json', filename: 'mmtp-channel-partner-api.json', label: 'OpenAPI JSON' },
  { id: 'openapi-yaml', href: '/openapi.yaml', filename: 'mmtp-channel-partner-api.yaml', label: 'OpenAPI YAML' },
  { id: 'postman', href: '/postman', filename: 'mmtp-channel-partner.postman_collection.json', label: 'Postman' },
  { id: 'guide', href: '/integration-guide', filename: 'CHANNEL_PARTNER_INTEGRATION_GUIDE.md', label: 'Integration guide' },
  { id: 'testdata', href: '/test-data', filename: 'sandbox-test-data.json', label: 'Test data' },
];

export const ApiDocsOverlay: React.FC = () => {
  const { apiBase, authHeaders, isMirror } = useCpPortal();
  const [section, setSection] = useState<Section>('overview');
  const [overview, setOverview] = useState<OverviewPayload | null>(null);
  const [html, setHtml] = useState<string | null>(null);
  const [testData, setTestData] = useState<TestDataPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const load = useCallback(async (sec: Section) => {
    setLoading(true);
    setError(null);
    try {
      if (sec === 'overview') {
        const res = await fetch(`${apiBase}/api-docs`, { headers: authHeaders() });
        const data = await res.json();
        if (!res.ok || !data.success) throw new Error(data.error || 'Failed to load API documentation');
        setOverview(data);
        setHtml(null);
      } else if (sec === 'reference') {
        const res = await fetch(`${apiBase}/api-docs/reference`, { headers: authHeaders() });
        const data = await res.json();
        if (!res.ok || !data.success) throw new Error(data.error || 'Failed to load API reference');
        setHtml(data.html);
      } else {
        const res = await fetch(`${apiBase}/api-docs/test-data`, { headers: authHeaders() });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error((data as { error?: string }).error || 'Failed to load sandbox test data');
        }
        setTestData(await res.json());
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load API documentation');
    } finally {
      setLoading(false);
    }
  }, [apiBase, authHeaders]);

  useEffect(() => {
    load(section);
  }, [load, section]);

  const download = useCallback(async (href: string, filename: string) => {
    setDownloadError(null);
    try {
      const res = await fetch(`${apiBase}/api-docs${href}`, { headers: authHeaders() });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as { error?: string }).error || 'Download failed');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setDownloadError(e instanceof Error ? e.message : 'Download failed');
    }
  }, [apiBase, authHeaders]);

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <div className="rounded-xl border border-[var(--border)] bg-[var(--card)] p-4">
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-[var(--primary)]/10 p-2">
            <BookOpen className="h-5 w-5 text-[var(--primary)]" aria-hidden />
          </div>
          <div>
            <h2 className="text-base font-semibold text-[var(--foreground)]">Channel Partner API</h2>
            <p className="mt-1 text-sm text-[var(--muted-foreground)]">
              HMAC server-to-server API for pass-through VAS and payment rails. Use your sandbox
              API key and secret from the Credentials Center. Wallet JWT endpoints are not part
              of this pack.
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {DOWNLOADS.map((d) => (
          <button
            key={d.id}
            type="button"
            disabled={isMirror}
            onClick={() => download(d.href, d.filename)}
            className="inline-flex items-center gap-2 rounded-lg border border-[var(--border)] px-3 py-2 text-xs font-medium text-[var(--foreground)] hover:bg-[var(--muted)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Download className="h-3.5 w-3.5" aria-hidden />
            {d.label}
          </button>
        ))}
      </div>
      {downloadError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{downloadError}</div>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setSection('overview')}
          className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
            section === 'overview'
              ? 'bg-[var(--primary)] text-[var(--primary-foreground)]'
              : 'border border-[var(--border)] text-[var(--foreground)] hover:bg-[var(--muted)]'
          }`}
        >
          <BookOpen className="h-4 w-4" aria-hidden /> Overview
        </button>
        <button
          type="button"
          onClick={() => setSection('reference')}
          className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
            section === 'reference'
              ? 'bg-[var(--primary)] text-[var(--primary-foreground)]'
              : 'border border-[var(--border)] text-[var(--foreground)] hover:bg-[var(--muted)]'
          }`}
        >
          <FileCode className="h-4 w-4" aria-hidden /> API Reference
        </button>
        <button
          type="button"
          onClick={() => setSection('testdata')}
          className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
            section === 'testdata'
              ? 'bg-[var(--primary)] text-[var(--primary-foreground)]'
              : 'border border-[var(--border)] text-[var(--foreground)] hover:bg-[var(--muted)]'
          }`}
        >
          <FlaskConical className="h-4 w-4" aria-hidden /> Test data
        </button>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>
      )}

      <div className="overflow-hidden rounded-xl border border-[var(--border)] bg-white">
        {loading ? (
          <div className="flex h-96 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-[var(--muted-foreground)]" aria-hidden />
          </div>
        ) : section === 'reference' && html ? (
          <iframe
            title="Channel Partner API reference"
            srcDoc={html}
            sandbox="allow-scripts allow-same-origin"
            className="h-[70vh] w-full border-0"
          />
        ) : section === 'overview' && overview ? (
          <div className="space-y-6 p-5 text-sm text-[var(--foreground)]">
            <p className="text-[var(--muted-foreground)]">
              {overview.summary} Spec version {overview.specVersion}.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              {(overview.servers || []).map((s) => (
                <div key={s.url} className="rounded-lg border border-[var(--border)] p-3">
                  <p className="font-semibold">{s.label}</p>
                  <code className="mt-1 block break-all font-mono text-xs">{s.url}</code>
                  {s.note && <p className="mt-2 text-xs text-[var(--muted-foreground)]">{s.note}</p>}
                </div>
              ))}
            </div>
            {overview.auth && (
              <div>
                <h3 className="font-semibold">HMAC authentication</h3>
                <ul className="mt-2 list-disc space-y-1 pl-5 text-[var(--muted-foreground)]">
                  <li><code>{overview.auth.authorizationHeader}</code></li>
                  <li><code>{overview.auth.timestampHeader}</code> (±{overview.auth.replayWindowSeconds}s)</li>
                  <li>HMAC key: <code>{overview.auth.hmacKey}</code></li>
                </ul>
              </div>
            )}
            {overview.sets && Object.keys(overview.sets).length > 0 && (
              <div className="grid gap-3 sm:grid-cols-2">
                {Object.entries(overview.sets).map(([key, set]) => (
                  <div key={key} className="rounded-lg border border-[var(--border)] p-3">
                    <p className="font-semibold">{set.label}</p>
                    <p className="mt-1 text-xs text-[var(--muted-foreground)]">{set.kyc}</p>
                    <p className="mt-1 font-mono text-[11px] text-[var(--muted-foreground)]">{set.gate}</p>
                  </div>
                ))}
              </div>
            )}
            {overview.operations && overview.operations.length > 0 && (
              <div>
                <h3 className="font-semibold">Operations</h3>
                <ul className="mt-2 divide-y divide-[var(--border)]">
                  {overview.operations.map((op) => (
                    <li key={`${op.method}-${op.path}`} className="flex flex-wrap items-baseline gap-2 py-2">
                      <span className="w-12 font-mono text-xs font-semibold uppercase">{op.method}</span>
                      <code className="font-mono text-xs">{op.path}</code>
                      <span className="text-xs text-[var(--muted-foreground)]">{op.summary}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : section === 'testdata' && testData ? (
          <div className="space-y-5 p-5 text-sm">
            <p className="text-[var(--muted-foreground)]">
              Standardised sandbox fixtures for MyMoolah testers and Channel Partners. Do not send
              these values to production.
            </p>
            {(testData.items || []).map((item) => (
              <div key={item.product} className="rounded-lg border border-[var(--border)] p-3">
                <p className="font-semibold text-[var(--foreground)]">{item.product}</p>
                <p className="mt-1 font-mono text-[11px] text-[var(--muted-foreground)]">
                  {item.endpoints.join(' · ')}
                </p>
                <dl className="mt-2 space-y-1">
                  {item.fields.map((f) => (
                    <div key={f.label} className="grid gap-1 sm:grid-cols-3">
                      <dt className="text-xs text-[var(--muted-foreground)]">{f.label}</dt>
                      <dd className="font-mono text-xs sm:col-span-2">{f.value}</dd>
                    </div>
                  ))}
                </dl>
                {item.note && <p className="mt-2 text-xs text-[var(--muted-foreground)]">{item.note}</p>}
              </div>
            ))}
          </div>
        ) : (
          <div className="flex h-96 items-center justify-center text-sm text-[var(--muted-foreground)]">
            Documentation not available.
          </div>
        )}
      </div>

      {isMirror && (
        <p className="text-xs text-[var(--muted-foreground)]">
          Read-only admin mirror.
        </p>
      )}
    </div>
  );
};
